import { apiFetch } from "@/lib/api";

export type SupplierListStatus = "active" | "deleted" | "all";

export interface MasterDataSupplierRow {
  id: number;
  mv_supplier_id: number;
  mv_supplier_name: string;
  mv_supplier_type: string | null;
  currency: string | null;
  payment_terms: string | null;
  manufacturing_subcontractor: boolean | null;
  megaventory_deleted_at: string | null;
  merged_into_supplier_id: number | null;
  merged_into_mv_supplier_id: number | null;
  merged_into_name: string | null;
  current_user_follows: boolean;
}

export interface SupplierMergeTarget {
  id: number;
  mv_supplier_id: number;
  mv_supplier_name: string;
}

export type FieldOwnershipMap = Record<string, string>;

export function isFieldReadOnly(ownership: FieldOwnershipMap, field: string): boolean {
  const controller = ownership[field];
  return controller === "freshsales" || controller === "erpnext" || controller === "quickbooks";
}

export function isFieldEditable(ownership: FieldOwnershipMap, field: string): boolean {
  const controller = ownership[field];
  return controller === "megaventory" || controller === "supabase";
}

export async function fetchSuppliers(input: {
  q?: string;
  status: SupplierListStatus;
}): Promise<{ rows: MasterDataSupplierRow[]; deleted_match_count: number }> {
  const params = new URLSearchParams();
  const query = input.q?.trim();
  if (query) {
    params.set("q", query);
  }
  params.set("status", input.status);
  return apiFetch<{ rows: MasterDataSupplierRow[]; deleted_match_count: number }>(
    `/api/mrp/suppliers?${params.toString()}`,
  );
}

export interface SupplierQuoteContactRow {
  hub_contact_id: number;
  email: string;
  display_name: string | null;
}

export interface SupplierFreshdeskCompanyRow {
  freshdesk_company_id: number;
  freshdesk_company_name: string;
}

export interface SupplierPurchaseOrderRow {
  id: number;
  po_number: string | null;
  hub_status: string;
  created_at: string | null;
}

export async function fetchSupplierCompanyContacts(supplierId: number): Promise<{
  rows: { id: number; name: string; email: string | null; job_title: string | null }[];
  freshdesk_company_id: number | null;
  load_error: string | null;
}> {
  return apiFetch(`/api/mrp/suppliers/${supplierId}/company-contacts`);
}

export async function fetchSupplierPurchaseOrders(supplierId: number): Promise<{
  rows: SupplierPurchaseOrderRow[];
}> {
  return apiFetch(`/api/mrp/suppliers/${supplierId}/purchase-orders`);
}

export async function fetchSupplierDetail(id: number): Promise<{
  supplier: Record<string, unknown>;
  merged_into: SupplierMergeTarget | null;
  sourcing_channel: "catalog_web" | "contract_manufacturer" | null;
  quote_contacts: SupplierQuoteContactRow[];
  freshdesk_company: SupplierFreshdeskCompanyRow | null;
  field_ownership: FieldOwnershipMap;
  purchase_orders: SupplierPurchaseOrderRow[];
}> {
  return apiFetch(`/api/mrp/suppliers/${id}`);
}

export async function searchFreshdeskCompanySuggestions(q: string): Promise<
  { id: number; name: string }[]
> {
  const data = await apiFetch<{ suggestions: { id: number; name: string }[] }>(
    `/api/mrp/suppliers/freshdesk-companies/suggestions?q=${encodeURIComponent(q)}`,
  );
  return data.suggestions;
}

export async function createSupplier(body: Record<string, unknown>): Promise<{
  supplier_id: number;
  mv_supplier_id: number;
  freshdesk_company_id: number;
}> {
  return apiFetch("/api/mrp/suppliers", {
    method: "POST",
    body,
  });
}

export async function patchSupplier(id: number, patch: Record<string, unknown>): Promise<unknown> {
  return apiFetch(`/api/mrp/suppliers/${id}`, {
    method: "PATCH",
    body: patch,
  });
}

export type SupplierArrivalFollowerRow = {
  userId: string;
  email: string;
  displayName: string | null;
};

export function supplierArrivalFollowersQueryKey(supplierId: number): (string | number)[] {
  return ["mrp-supplier-arrival-followers", supplierId];
}

export async function fetchSupplierArrivalFollowers(supplierId: number): Promise<{
  followers: SupplierArrivalFollowerRow[];
  current_user_follows: boolean;
}> {
  const data = await apiFetch<{
    followers: { user_id?: string; userId?: string; email: string; display_name?: string | null; displayName?: string | null }[];
    current_user_follows: boolean;
  }>(`/api/mrp/suppliers/${supplierId}/arrival-followers`);
  return {
    current_user_follows: data.current_user_follows,
    followers: data.followers.map((row) => ({
      userId: row.userId ?? row.user_id ?? "",
      email: row.email,
      displayName: row.displayName ?? row.display_name ?? null,
    })),
  };
}

export async function replaceSupplierArrivalFollowers(
  supplierId: number,
  userIds: string[],
): Promise<{ followers: SupplierArrivalFollowerRow[] }> {
  const data = await apiFetch<{
    followers: { user_id?: string; userId?: string; email: string; display_name?: string | null; displayName?: string | null }[];
  }>(`/api/mrp/suppliers/${supplierId}/arrival-followers`, {
    method: "PUT",
    body: { userIds },
  });
  return {
    followers: data.followers.map((row) => ({
      userId: row.userId ?? row.user_id ?? "",
      email: row.email,
      displayName: row.displayName ?? row.display_name ?? null,
    })),
  };
}

export async function setSelfFollowsSupplierArrival(
  supplierId: number,
  following: boolean,
): Promise<{ current_user_follows: boolean }> {
  return apiFetch(`/api/mrp/suppliers/${supplierId}/arrival-followers/self`, {
    method: "PUT",
    body: { following },
  });
}

export interface PurchaseOrderFinancialDocLineInput {
  purchase_order_item_id: number;
  quantity: number;
  unit_price?: number | null;
}

export interface PurchaseInvoiceTshLineInput {
  sku: string;
  quantity: number;
  unit_price?: number | null;
  description?: string | null;
}

export async function createTshFromPurchaseInvoice(
  purchaseInvoiceId: number,
  lines: PurchaseInvoiceTshLineInput[],
): Promise<unknown> {
  return apiFetch(`/api/mrp/financial-documents/tsh/from-purchase-invoice/${purchaseInvoiceId}`, {
    method: "POST",
    body: { lines },
  });
}

export async function createGosiFromSalesOrder(
  salesOrderId: number,
): Promise<{ mv_document_id: number; ingest_entity: string }> {
  return apiFetch(`/api/mrp/financial-documents/gosi/from-sales-order/${salesOrderId}`, {
    method: "POST",
  });
}

export async function cancelFinancialDocument(
  mvDocumentId: number,
  ingestEntity: "gipi" | "tsh" | "gosi" | "wofg",
): Promise<unknown> {
  return apiFetch(`/api/mrp/financial-documents/${mvDocumentId}/cancel`, {
    method: "POST",
    body: { ingest_entity: ingestEntity },
  });
}
